import type { GetStaticProps } from "next";
import Link from "next/link";
import { FooterSite, HeaderSite, SeoHead } from "@acme/feature/landing";
import { compareSlug, formatContext, formatMonth, getActiveModelPages, getComparePairs, getModelPage, type ModelPageData } from "../../lib/model-pages";

type Props = {
    groups: { provider: string; providerName: string; models: ModelPageData[] }[];
    compares: { slug: string; label: string }[];
    count: number;
};

export default function ModelsDirectory({ groups, compares, count }: Props) {
    return (
        <>
            <SeoHead
                path="/models"
                title={`AI Model Directory: ${count} Models from 8 Providers | DapperGPT`}
                description="Context windows, output limits, release dates and capabilities for every current GPT, Claude, Gemini, Grok, DeepSeek, GLM, Kimi and Qwen model. Chat with any of them using your own API key."
            />
            <div className="bg-white dark:bg-gray-900 min-h-screen flex flex-col text-gray-900 dark:text-gray-100">
                <HeaderSite containerClassName="!max-w-7xl px-6" />
                <main className="flex-grow container mx-auto max-w-6xl px-4 py-14">
                    <h1 className="text-4xl md:text-5xl font-bold tracking-tight">AI model directory</h1>
                    <p className="mt-4 max-w-2xl text-lg text-gray-600 dark:text-gray-400">
                        Specs for the {count} current models you can use in DapperGPT, newest first. Bring your own key from any provider and switch models in any chat.
                    </p>

                    {groups.map(group => (
                        <section key={group.provider} className="mt-12" aria-labelledby={`p-${group.provider}`}>
                            <h2 id={`p-${group.provider}`} className="text-xl font-semibold">{group.providerName}</h2>
                            <div className="mt-4 overflow-x-auto">
                                <table className="w-full min-w-[560px] text-sm">
                                    <thead>
                                        <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
                                            <th className="font-medium py-2">Model</th>
                                            <th className="font-medium py-2">Released</th>
                                            <th className="font-medium py-2 text-right">Context</th>
                                            <th className="font-medium py-2 text-right">Max output</th>
                                            <th className="font-medium py-2 text-right">Reasoning</th>
                                        </tr>
                                    </thead>
                                    <tbody className="tabular-nums">
                                        {group.models.map(m => (
                                            <tr key={m.id} className="border-t border-gray-100 dark:border-gray-800">
                                                <td className="py-3 pr-4">
                                                    <Link href={`/models/${m.id}`} className="font-medium text-blue-700 dark:text-blue-400 hover:underline">{m.name}</Link>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">{m.description}</p>
                                                </td>
                                                <td className="py-3 pr-4 whitespace-nowrap text-gray-600 dark:text-gray-300">{formatMonth(m.releaseDate)}</td>
                                                <td className="py-3 text-right">{formatContext(m.contextWindow)}</td>
                                                <td className="py-3 text-right">{formatContext(m.maxOutputTokens)}</td>
                                                <td className="py-3 text-right">{m.reasoning ? 'Yes' : 'No'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    ))}

                    <section className="mt-16">
                        <h2 className="text-xl font-semibold">Head-to-head comparisons</h2>
                        <ul className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2 list-none p-0">
                            {compares.map(c => (
                                <li key={c.slug} className="list-none">
                                    <Link href={`/compare/${c.slug}`} className="block rounded-lg border border-gray-200 dark:border-gray-800 px-4 py-3 text-sm hover:border-blue-500">{c.label}</Link>
                                </li>
                            ))}
                        </ul>
                    </section>

                    <div className="mt-16 rounded-2xl bg-gray-900 dark:bg-gray-800 text-white p-8 text-center">
                        <p className="text-2xl font-semibold">Try them side by side</p>
                        <p className="mt-2 text-gray-300">Add a key from any provider and switch models mid-conversation.</p>
                        <Link href="/chat" className="mt-5 inline-block rounded-lg bg-white text-gray-900 px-6 py-3 font-semibold hover:bg-gray-100">Start chatting free</Link>
                    </div>
                </main>
                <FooterSite />
            </div>
        </>
    );
}

export const getStaticProps: GetStaticProps<Props> = () => {
    const models = getActiveModelPages();
    const groups: Props['groups'] = [];
    for (const m of models) {
        const g = groups.find(x => x.provider === m.provider);
        if (g) g.models.push(m);
        else groups.push({ provider: m.provider, providerName: m.providerName, models: [m] });
    }
    const compares = getComparePairs().map(([a, b]) => ({
        slug: compareSlug(a, b),
        label: `${getModelPage(a)!.name} vs ${getModelPage(b)!.name}`,
    }));
    return { props: { groups, compares, count: models.length } };
};
